import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";

import { SliderPageWrapper } from "../../../layout/sliderPageWrapper/SliderPageWrapper";

import { useAssets } from "../../../../hooks/useAssets";
import { useRootDispatch, useRootSelector } from "../../../../store/hooks";
import { updateSlide } from "../../../../store/common/common-slice";
import { resetInfo } from "store/payments/payments-slice";
import {
  fetchPayments,
  FetchPayments,
} from "store/paymentActivity/paymentActivity-action";

import { QuickPayPayment } from "components/pdf/QuickPayPayment";

import classes from "./Confirmation.module.scss";
import { isFuture, isToday } from "date-fns";
import { useRequestDispatch } from "store/requestHooks";
import {
  FetchSchedulePayments,
  fetchSchedulePayments,
} from "store/schedulePayments/schedulePayments-action";
import { selectedAccountInfo } from "store/account/account-selector";
import { FilterProps } from "store/common";
import { ScheduledPaymentsFilter } from "store/schedulePayments/schedulePayments-state";

export interface Confirmationprops {}

export const Confirmation: React.FC<Confirmationprops> = () => {
  const { t } = useTranslation(["common", "payment", "payment_final"]);

  const dispatch = useRootDispatch();
  const requestDispatch = useRequestDispatch();

  const { createAssetUrl } = useAssets();

  const paymentData = useRootSelector((state) => state.payments.paymentData);
  const accountInfo = useRootSelector(selectedAccountInfo);

  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => (componentRef.current ? componentRef.current : null),
  });

  useEffect(() => {
    return () => {
      dispatch(resetInfo());
      requestDispatch<FetchPayments, FilterProps>(fetchPayments, {
        accountNumber: accountInfo?.accountNumber,
      });
      requestDispatch<FetchSchedulePayments, ScheduledPaymentsFilter>(
        fetchSchedulePayments,
        {
          accountInformationId: accountInfo?.id!,
          status: [0, 1],
        },
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <SliderPageWrapper headerTitle={t("common:make_payment_label")}>
        <div className={classes.Container}>
          <div className={classes.Image_container}>
            <img
              alt="confirmation"
              src={createAssetUrl("successful-action")}
            ></img>
          </div>
          <div className={classes.Main}>
            <p className={classes.Heading}>
              {t("payment_final:congratulations")}
            </p>
            <p className={classes.Heading}>
              {t("payment_final:payment_complete")}
            </p>
            <div className={classes.Detail}>
              <p>
                On{" "}
                {t("common:date_time_format", {
                  value: paymentData?.paymentDate,
                })}
              </p>
            </div>
            <div className={classes.Confirmation_detail}>
              <div className={classes.Items}>
                <p className={classes.Items_heading}>
                  {t("payment:confirmationNumber")}
                </p>
                <p data-testid="payment_nextPage-makePayment-confirmation-confirmationNumber">
                  {isToday(new Date(paymentData?.paymentDate!))
                    ? paymentData?.paymentResponse?.id
                    : "-"}
                </p>
              </div>
              <div className={classes.Items}>
                <p className={classes.Items_heading}>
                  {t("payment_final:payment_amount")}
                </p>
                <p>
                  {t("common:currency_format", {
                    value: +(paymentData?.paymentAmount ?? 0),
                  })}
                </p>
              </div>
              {paymentData?.feeTotal != null && paymentData.feeTotal > 0 ? (
                <>
                  <div className={classes.Items}>
                    <p className={classes.Items_heading}>
                      {t("payment_final:convenience_fee_label")}
                    </p>
                    <p>
                      {t("common:currency_format", {
                        value: paymentData.feeTotal,
                      })}
                    </p>
                  </div>
                  <div className={classes.Items}>
                    <p className={classes.Items_heading}>
                      {t("payment_final:total_payment")}
                    </p>
                    <p>
                      {t("common:currency_format", {
                        value:
                          paymentData.feeTotal +
                          +(paymentData.paymentAmount ?? 0),
                      })}
                    </p>
                  </div>
                </>
              ) : null}
              {paymentData?.paymentDate &&
              !isFuture(new Date(paymentData?.paymentDate)) ? (
                <ft-icon
                  onClick={handlePrint}
                  aria-label={t("common:download")}
                  title={t("common:download")}
                >
                  file_download
                </ft-icon>
              ) : null}
            </div>
            <div className={classes.Buttons}>
              <ft-button
                class="outline-button"
                outlined
                onClick={() => {
                  dispatch(updateSlide({ id: "ManageWallet", tab: "1" }));
                }}
                label={t("payment_final:enroll_in_autopay")}
              ></ft-button>
            </div>
          </div>
        </div>
      </SliderPageWrapper>
      <div className={classes.PrintCss}>
        <QuickPayPayment
          ref={componentRef}
          paymentData={paymentData ? paymentData : undefined}
        ></QuickPayPayment>
      </div>
    </>
  );
};
